import React, { useState, useEffect } from 'react';
import { useProjectStore, usePoleStore, useCustomerStore } from '../../stores';
import { useONUStore } from '../../stores/onuStore';
import { ONUWithDetails, ONUStatus } from '../../types';
import { getErrorMessage } from '../../utils/errorMessages';
import './Dialog.css';

interface EditONUDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onu: ONUWithDetails;
}

export const EditONUDialog: React.FC<EditONUDialogProps> = ({ isOpen, onClose, onu }) => {
  const { currentProject } = useProjectStore();
  const { updateONU, fetchONUs } = useONUStore();
  const { poles, fetchPoles } = usePoleStore();
  const { customers, fetchCustomers } = useCustomerStore();

  const [name, setName] = useState(onu.name);
  const [customerId, setCustomerId] = useState<number | undefined>(onu.customer_id);
  const [poleId, setPoleId] = useState(onu.pole_id);
  const [splitterIndex, setSplitterIndex] = useState<number | undefined>(onu.splitter_index);
  const [splitterPort, setSplitterPort] = useState<number | undefined>(onu.splitter_port);
  const [fiberColor, setFiberColor] = useState(onu.fiber_color || '');
  const [onuModel, setOnuModel] = useState(onu.onu_model || '');
  const [serialNumber, setSerialNumber] = useState(onu.serial_number || '');
  const [macAddress, setMacAddress] = useState(onu.mac_address || '');
  const [status, setStatus] = useState<ONUStatus>(onu.status);
  const [notes, setNotes] = useState(onu.notes || '');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchPoles(currentProject?.id || 0);
      fetchCustomers();
    }
  }, [isOpen]);

  useEffect(() => {
    setName(onu.name);
    setCustomerId(onu.customer_id);
    setPoleId(onu.pole_id);
    setSplitterIndex(onu.splitter_index);
    setSplitterPort(onu.splitter_port);
    setFiberColor(onu.fiber_color || '');
    setOnuModel(onu.onu_model || '');
    setSerialNumber(onu.serial_number || '');
    setMacAddress(onu.mac_address || '');
    setStatus(onu.status);
    setNotes(onu.notes || '');
  }, [onu]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!currentProject) {
        throw new Error('Nie je vybraný projekt');
      }

      if (!name.trim()) {
        throw new Error('Názov je povinný');
      }

      await updateONU(onu.id, {
        name: name.trim(),
        customer_id: customerId,
        pole_id: poleId,
        splitter_index: splitterIndex,
        splitter_port: splitterPort,
        fiber_color: fiberColor || undefined,
        onu_model: onuModel || undefined,
        serial_number: serialNumber || undefined,
        mac_address: macAddress || undefined,
        status,
        notes: notes || undefined,
      });

      await fetchONUs(currentProject.id);
      onClose();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa aktualizovať ONU.'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const polesWithCustomerSplitters = poles.filter(
    (p) => p.cassette?.splitters?.some((s) => s.is_customer_splitter)
  );

  const selectedPole = poles.find((p) => p.id === poleId);
  const availableSplitters = selectedPole?.cassette?.splitters?.filter((s) => s.is_customer_splitter) || [];

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>✏️ Upraviť ONU</h2>
          <button className="dialog-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-content">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label>Názov *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="napr. ONU-001"
            />
          </div>

          <div className="form-group">
            <label>Zákazník</label>
            <select
              value={customerId || ''}
              onChange={(e) => setCustomerId(e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">-- Bez zákazníka --</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.customer_type === 'individual'
                    ? `${customer.first_name} ${customer.last_name}`
                    : customer.company_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Stĺp so zákazníckym splittrom *</label>
            <select
              value={poleId}
              onChange={(e) => {
                setPoleId(parseInt(e.target.value));
                setSplitterIndex(undefined);
                setSplitterPort(undefined);
              }}
              required
            >
              <option value={0}>-- Vyberte stĺp --</option>
              {polesWithCustomerSplitters.map((pole) => {
                const customerSplitters = pole.cassette?.splitters?.filter((s) => s.is_customer_splitter) || [];
                return (
                  <option key={pole.id} value={pole.id}>
                    {pole.name} ({customerSplitters.length} zákaznícky{customerSplitters.length === 1 ? '' : 'ch'} splitter{customerSplitters.length === 1 ? '' : 'ov'})
                  </option>
                );
              })}
            </select>
          </div>

          {availableSplitters.length > 0 && (
            <>
              <div className="form-group">
                <label>Splitter</label>
                <select
                  value={splitterIndex ?? ''}
                  onChange={(e) => {
                    setSplitterIndex(e.target.value ? parseInt(e.target.value) : undefined);
                    setSplitterPort(undefined);
                  }}
                >
                  <option value="">-- Vyberte splitter --</option>
                  {availableSplitters.map((splitter, idx) => (
                    <option key={idx} value={idx}>
                      Splitter {idx + 1} ({splitter.type})
                    </option>
                  ))}
                </select>
              </div>

              {splitterIndex !== undefined && availableSplitters[splitterIndex] && (
                <div className="form-group">
                  <label>Port</label>
                  <select
                    value={splitterPort ?? ''}
                    onChange={(e) => setSplitterPort(e.target.value ? parseInt(e.target.value) : undefined)}
                  >
                    <option value="">-- Vyberte port --</option>
                    {Array.from({ length: availableSplitters[splitterIndex].outputs?.length || 0 }, (_, i) => i + 1).map((portNum) => (
                      <option key={portNum} value={portNum}>
                        Port {portNum}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          <div className="form-group">
            <label>Farba vlákna</label>
            <select
              value={fiberColor}
              onChange={(e) => setFiberColor(e.target.value)}
            >
              <option value="">-- Vyberte farbu --</option>
              <option value="blue">🔵 Modrá (Blue)</option>
              <option value="orange">🟠 Oranžová (Orange)</option>
              <option value="green">🟢 Zelená (Green)</option>
              <option value="brown">🟤 Hnedá (Brown)</option>
              <option value="gray">⚪ Sivá (Gray)</option>
              <option value="white">⚪ Biela (White)</option>
              <option value="red">🔴 Červená (Red)</option>
              <option value="black">⚫ Čierna (Black)</option>
              <option value="yellow">🟡 Žltá (Yellow)</option>
              <option value="violet">🟣 Fialová (Violet)</option>
              <option value="pink">🩷 Ružová (Pink)</option>
              <option value="turquoise">🩵 Tyrkysová (Turquoise)</option>
            </select>
          </div>

          <div className="form-group">
            <label>Model zariadenia</label>
            <input
              type="text"
              value={onuModel}
              onChange={(e) => setOnuModel(e.target.value)}
              placeholder="napr. Huawei HG8310M"
            />
          </div>

          <div className="form-group">
            <label>Sériové číslo</label>
            <input
              type="text"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="napr. HWTC12345678"
            />
          </div>

          <div className="form-group">
            <label>MAC adresa</label>
            <input
              type="text"
              value={macAddress}
              onChange={(e) => setMacAddress(e.target.value)}
              placeholder="napr. 00:11:22:33:44:55"
            />
          </div>

          <div className="form-group">
            <label>Stav</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as ONUStatus)}
            >
              <option value="pending">⏳ Pending</option>
              <option value="active">✅ Active</option>
              <option value="inactive">⏸️ Inactive</option>
              <option value="offline">❌ Offline</option>
            </select>
          </div>

          <div className="form-group">
            <label>Poznámky</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Poznámky k ONU..."
            />
          </div>

          <div className="dialog-actions">
            <button type="button" onClick={onClose} disabled={loading}>
              Zrušiť
            </button>
            <button type="submit" disabled={loading}>
              {loading ? 'Ukladám...' : 'Uložiť'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

